import React from 'react';
import { ExternalLink } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const SponsorsPage = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();

  const txt = {
    en: {
      title: 'Infrastructure Sponsors',
      subtitle: 'Organizations providing infrastructure and resources to power Threat.live detection nodes worldwide.',
      visitWebsite: 'Visit Website',
    },
    tr: {
      title: 'Altyapı Sponsorları',
      subtitle: 'Threat.live algılama düğümlerine dünya çapında güç sağlayan altyapı ve kaynakları sunan kuruluşlar.',
      visitWebsite: 'Web Sitesini Ziyaret Et',
    },
  };

  const t = txt[language] || txt.en;

  const sponsors = [
    {
      name: 'Tres Teknoloji',
      url: 'https://www.tresteknoloji.com',
      logoDark: 'https://www.tresteknoloji.com/images/tres-logo-dark.svg',
      logoLight: 'https://www.tresteknoloji.com/images/tres-logo-light.svg',
    },
    {
      name: 'Netlen',
      url: 'https://www.netlen.com.tr',
      logoDark: 'https://www.netlen.com.tr/netlen-logo.svg',
      logoLight: 'https://www.netlen.com.tr/images/Netlen-Logo-Light.svg',
    },
    {
      name: 'Alastyr',
      url: 'https://www.alastyr.com',
      logoDark: 'https://www.alastyr.com/assets/img/logo.svg',
      logoLight: 'https://www.alastyr.com/assets/img/logo-white.svg',
      useInvert: false,
    },
    {
      name: 'Netdirekt',
      url: 'https://www.netdirekt.com.tr',
      logoDark: 'https://www.netdirekt.com.tr/images/netdirektlogo3_1_260x64.webp',
      logoLight: 'https://www.netdirekt.com.tr/images/netdirektlogo3_1_260x64.webp',
    },
    {
      name: 'GıbırNet İnternet',
      url: 'https://gibir.net.tr',
      logoDark: 'https://gibir.net.tr/_next/image?url=%2Fimages%2Flogos%2Fgibir-black-logo.png&w=256&q=75',
      logoLight: 'https://gibir.net.tr/_next/image?url=%2Fimages%2Flogos%2Fgibir-white-logo.png&w=256&q=75',
    },
    {
      name: 'Datacasa',
      url: 'https://datacasa.com.tr',
      logoDark: 'https://cdn.netlen.com.tr/2026/05/17/datacasa-logo.svg',
      logoLight: 'https://cdn.netlen.com.tr/2026/05/17/datacasa-logo-white.png',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        page="sponsors"
        title={t.title}
        description={t.subtitle}
      />
      
      <Header />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="soc-badge mx-auto mb-6">
            <span className="status-dot" />
            <span>// SPONSORS · INFRASTRUCTURE</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {t.title}
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto border-l-2 border-primary pl-4 text-left">
            {t.subtitle}
          </p>
        </div>

        {/* Sponsors Grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sponsors.map((sponsor, index) => (
              <a
                key={index}
                href={sponsor.url}
                target="_blank"
                rel="noopener"
                className="group relative"
              >
                <div className={`sharp border border-border bg-card/50 backdrop-blur p-8 h-40 flex items-center justify-center transition-all duration-300 hover:border-primary hover:bg-card hover:shadow-lg hover:shadow-primary/10 ${
                  sponsor.darkTileInLight && theme !== 'dark' ? 'bg-zinc-900 border-zinc-800' : ''
                }`}>
                  <img
                    src={theme === 'dark' && sponsor.logoLight ? sponsor.logoLight : sponsor.logoDark}
                    alt={`${sponsor.name} logo`}
                    className={`max-w-full max-h-16 object-contain transition-all duration-300 ${
                      sponsor.useInvert && theme === 'dark' ? 'invert' : ''
                    } group-hover:scale-110`}
                    loading="lazy"
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 sharp bg-primary/95 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                    <div className="flex items-center gap-2 text-white font-mono text-xs uppercase tracking-wider">
                      <ExternalLink className="h-4 w-4" />
                      <span>{t.visitWebsite}</span>
                    </div>
                  </div>
                </div>
                
                {/* Company Name */}
                <div className="mt-3 text-center">
                  <p className="text-sm font-mono text-muted-foreground group-hover:text-foreground transition-colors">
                    {sponsor.name}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Thank You Note */}
        <div className="max-w-4xl mx-auto mt-16 p-6 sharp border border-primary/20 bg-primary/5">
          <p className="text-center text-sm text-muted-foreground">
            {language === 'tr' 
              ? 'Threat.live topluluğuna katkılarınız için teşekkür ederiz. Sizler sayesinde internet daha güvenli hale geliyor.' 
              : 'Thank you for your contributions to the Threat.live community. Together, we make the internet safer.'}
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SponsorsPage;
